import { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import { toast } from "sonner";
import { TrendUp, TrendDown, ArrowRight, Broadcast, Plant, MapTrifold, StackSimple } from "@phosphor-icons/react";
import { getEeChange, getMunicipio, getEeLandcover } from "@/lib/api";
import { Sheet, SheetToggles } from "@/components/rs/mobileSheet";

const YEARS = Array.from({ length: 25 }, (_, i) => 2000 + i);

const BASEMAPS = {
  satelite: { label: "Satélite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Teselas &copy; <a href='https://www.esri.com' target='_blank'>Esri</a> — Fuente: Esri, Maxar, Earthstar Geographics, GIS User Community", cls: "" },
  gris: { label: "Gris", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; <a href='https://www.openstreetmap.org/copyright' target='_blank'>OpenStreetMap</a> contributors (ODbL)", cls: "cm-gray" },
  claro: { label: "Claro", url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png", attribution: "&copy; <a href='https://www.openstreetmap.org/copyright' target='_blank'>OpenStreetMap</a> contributors, &copy; <a href='https://carto.com/attributions' target='_blank'>CARTO</a>", cls: "" },
};

function YSel({ value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId}
      className="w-full bg-[#f8faf9] border border-[#e5e7eb] rounded-md px-2.5 py-2 text-sm mono text-[#1f2937] focus:border-[#4a9b3c] outline-none [color-scheme:dark]">
      {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}

export default function ChangeViewEE() {
  const [yearA, setYearA] = useState(2000);
  const [yearB, setYearB] = useState(2024);
  const [agriOnly, setAgriOnly] = useState(true);
  const [basemap, setBasemap] = useState("satelite");
  const [showAgri, setShowAgri] = useState(true);
  const [cropTile, setCropTile] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [muni, setMuni] = useState(null);
  const [sheet, setSheet] = useState(null);

  useEffect(() => { getMunicipio().then(setMuni).catch(() => {}); }, []);
  useEffect(() => { getEeLandcover(2021).then((d) => setCropTile(d.crop_tile_url)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true); setData(null);
    getEeChange(yearA, yearB, "ndvi", "landsat", agriOnly)
      .then(setData).catch((e) => toast.error(e?.response?.data?.detail || "Error de Earth Engine"))
      .finally(() => setLoading(false));
  }, [yearA, yearB, agriOnly]);

  const s = data?.stats;
  const declining = s ? s.net_ha < 0 : false;
  const scope = agriOnly ? "zonas agrícolas (cultivos)" : "todo el municipio";

  return (
    <div className="flex-1 flex min-h-0">
      <Sheet open={sheet === "controls"} side="left" testid="change-sidebar" className="lg:w-[300px] space-y-5">
        <div>
          <div className="plex text-sm font-medium mb-1">Cambios de NDVI</div>
          <p className="text-[11px] text-[#475569] leading-relaxed">
            Diferencia de NDVI (B − A) con datos reales de Landsat 30 m, restringible a las zonas
            agrícolas (cobertura del suelo ESA WorldCover 10 m, Sentinel-2).
          </p>
        </div>
        <section className="space-y-3">
          <div><div className="label-caps mb-1">Año base (A)</div><YSel value={yearA} onChange={setYearA} testId="change-year-a" /></div>
          <div><div className="label-caps mb-1 text-[#3f8e33]">Año comparado (B)</div><YSel value={yearB} onChange={setYearB} testId="change-year-b" /></div>
        </section>
        <section>
          <div className="label-caps mb-2">Ámbito del cálculo</div>
          <div className="flex gap-2">
            <button onClick={() => setAgriOnly(true)} data-testid="change-agri-only"
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border transition-colors ${agriOnly ? "border-[#4a9b3c] bg-[#eef7ed] text-[#2d6624]" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
              <Plant size={13} /> Solo agrícola
            </button>
            <button onClick={() => setAgriOnly(false)} data-testid="change-all-muni"
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border transition-colors ${!agriOnly ? "border-orange-600 bg-orange-950/40 text-orange-300" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
              <MapTrifold size={13} /> Municipio
            </button>
          </div>
          <p className="text-[10px] text-[#475569] mt-2">
            {agriOnly ? "El cambio se calcula únicamente sobre las tierras de cultivo (enfoque del estudio)." : "El cambio se calcula sobre toda la superficie del municipio."}
          </p>
        </section>

        <section>
          <div className="label-caps mb-2 flex items-center gap-1.5"><StackSimple size={13} /> Mapa base</div>
          <div className="grid grid-cols-3 gap-1.5">
            {Object.entries(BASEMAPS).map(([k, b]) => (
              <button key={k} onClick={() => setBasemap(k)} data-testid={`change-basemap-${k}`}
                className={`text-[11px] py-1.5 rounded-md border transition-colors ${basemap === k ? "border-[#4a9b3c] bg-[#eef7ed] text-[#2d6624]" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
                {b.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 mt-3 text-[12px] text-[#1f2937] cursor-pointer" data-testid="change-show-agri">
            <input type="checkbox" checked={showAgri} onChange={(e) => setShowAgri(e.target.checked)}
              className="accent-fuchsia-500" />
            Resaltar zonas agrícolas (cultivos)
          </label>
        </section>
      </Sheet>

      <main className="flex-1 relative min-w-0" data-testid="change-map-area">
        {loading && (
          <div className="absolute inset-0 z-[600] flex items-center justify-center bg-[#f8faf9]/75 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <Broadcast size={26} className="text-[#3f8e33] animate-pulse" />
              <span className="text-xs mono text-[#334155]">Calculando cambios en Earth Engine…</span>
            </div>
          </div>
        )}
        {muni && (
          <div data-testid="change-map" className="change-map" style={{ height: "100%", width: "100%" }}>
          <MapContainer center={[muni.center.lat, muni.center.lon]} zoom={11}
            bounds={[[muni.bbox.lat_min, muni.bbox.lon_min], [muni.bbox.lat_max, muni.bbox.lon_max]]}
            style={{ height: "100%", width: "100%" }}>
            <TileLayer key={basemap} url={BASEMAPS[basemap].url} subdomains="abc"
              className={BASEMAPS[basemap].cls} attribution={BASEMAPS[basemap].attribution} maxZoom={18} />
            {showAgri && cropTile && <TileLayer key={`crop-${cropTile}`} url={cropTile} opacity={0.55} />}
            {data?.tile_url && <TileLayer key={data.tile_url} url={data.tile_url} opacity={0.9}
              attribution="Cambio NDVI: Landsat C2 L2 — USGS/NASA vía Google Earth Engine" />}
            <GeoJSON data={muni.geojson} style={{ color: "#22d3ee", weight: 2, fill: false, dashArray: "6 4" }} />
          </MapContainer>
          </div>
        )}
        {data?.vis && (
          <div className="absolute bottom-5 left-5 z-[500] glass rounded-md px-4 py-3 w-[250px]" data-testid="change-legend">
            <div className="label-caps mb-2">Cambio de NDVI (B − A)</div>
            <div className="h-2.5 w-full rounded-full" style={{ background: `linear-gradient(90deg, ${data.vis.palette.join(",")})` }} />
            <div className="flex justify-between mt-1.5 mono text-[11px] text-[#1f2937]"><span>{data.vis.min}</span><span>0</span><span>+{data.vis.max}</span></div>
            <div className="flex justify-between mt-0.5"><span className="text-[9px] uppercase text-amber-600">Disminución de NDVI</span><span className="text-[9px] uppercase text-green-500">Aumento de NDVI</span></div>
          </div>
        )}
        <div className="absolute top-4 left-[56px] z-[500] glass rounded-md px-3 py-1.5 text-[11px] mono text-[#1f2937] flex items-center gap-2 whitespace-nowrap">
          <span>{yearA}</span><ArrowRight size={12} /><span className="text-[#3f8e33]">{yearB}</span>
          <span className="text-[#475569]">· {agriOnly ? "agrícola" : "municipio"}</span>
        </div>
      </main>

      <Sheet open={sheet === "data"} side="right" testid="change-stats" className="lg:w-[340px] space-y-4">
        <div className="plex text-sm font-medium">Balance de cambios · {agriOnly ? "agrícola" : "municipio"}</div>
        {s && (
          <>
            <div className="border border-[#e5e7eb] p-4 bg-[#f8faf9]" data-testid="change-net">
              <div className="label-caps flex items-center gap-1.5">
                {declining ? <TrendDown size={13} className="text-amber-500" /> : <TrendUp size={13} className="text-green-500" />}
                Balance neto de NDVI
              </div>
              <div className="mono text-3xl font-semibold mt-1" style={{ color: declining ? "#d99a5b" : "#22c55e" }}>
                {s.net_ha > 0 ? "+" : ""}{s.net_ha} <span className="text-sm text-[#475569]">ha</span>
              </div>
              <div className="text-[11px] text-[#475569] mt-1">{declining ? "Predomina la disminución de NDVI" : "Predomina el aumento de NDVI"}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-[#e5e7eb] p-3 bg-[#f8faf9]" data-testid="change-gain">
                <div className="label-caps text-green-500">Aumento de NDVI</div>
                <div className="mono text-xl text-green-400">{s.gain_ha} <span className="text-xs text-[#475569]">ha</span></div>
              </div>
              <div className="border border-[#e5e7eb] p-3 bg-[#f8faf9]" data-testid="change-loss">
                <div className="label-caps text-amber-500">Disminución de NDVI</div>
                <div className="mono text-xl text-amber-400">{s.loss_ha} <span className="text-xs text-[#475569]">ha</span></div>
              </div>
            </div>
            <div className="border border-[#e5e7eb] p-3 bg-[#f8faf9]">
              <div className="label-caps">Cambio medio de NDVI</div>
              <div className="mono text-xl text-[#1f2937]">{s.mean_delta > 0 ? "+" : ""}{s.mean_delta}</div>
            </div>
            <div className="text-[11px] text-[#475569] leading-relaxed pt-2 border-t border-[#e5e7eb]">
              Umbral ±0.05 NDVI. {data.sensor}. Cálculo sobre {scope} vía Earth Engine.
              Las etiquetas describen variaciones del índice NDVI, no una medición directa de cobertura de cultivos.
            </div>
          </>
        )}
        {!s && !loading && <div className="text-[12px] text-[#475569]">Sin datos.</div>}
      </Sheet>
      <SheetToggles sheet={sheet} setSheet={setSheet} />
    </div>
  );
}
